/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useState, useEffect } from "react";
import { Draggable, Droppable } from "react-beautiful-dnd";
import Icon from "react-icons-kit";
import { trash } from "react-icons-kit/fa/trash";
import { clone } from "react-icons-kit/fa/clone";
import { pencil } from "react-icons-kit/fa/pencil";
import { Dimensions, TouchableOpacity, View, Platform } from "react-native";
import { Button, Text } from "UpduoLibrary";
import { NavLink } from "react-router-dom";

import {
  skaleLightGray,
  skaleMainBlue,
  skaleYellow,
  skaleWebBackgroundGray,
  skaleWebTextGray,
  skaleRed,
  skaleLinkBlue,
  skalePrimaryBlue,
  skaleTextGray,
  skaleWhite,
} from "config/UpduoColors";
import SkillListing from "./SkillListing";

import * as UpduoAnalytics from "config/UpduoAnalytics";

function SkillDeleteButton({ disabled, onPress }) {
  const [isHoveringDelete, setIsHoveringDelete] = useState<boolean>(false);

  const buttonColor = isHoveringDelete ? skaleRed : skaleMainBlue;
  return (
    <TouchableOpacity
      style={{
        alignItems: "center",
        justifyContent: "center",
        paddingHorizontal: 9,
      }}
      disabled={disabled}
      onPress={onPress}
      {...Platform.select({
        web: {
          onMouseEnter: () => {
            setIsHoveringDelete(true);
          },
          onMouseLeave: () => {
            setIsHoveringDelete(false);
          },
        },
      })}
    >
      <div
        style={{
          color: disabled ? skaleLightGray : buttonColor,
          paddingBottom: 2.5,
        }}
      >
        <Icon size={15} icon={trash} />
      </div>
    </TouchableOpacity>
  );
}

function SkillEditButton({ disabled, skillModule }) {
  const [isHoveringEdit, setIsHoveringEdit] = useState<boolean>(false);

  const buttonColor = isHoveringEdit ? skaleRed : skaleMainBlue;
  return (
    <NavLink
      to={`/content/topic/${skillModule.id}`}
      style={{
        textDecoration: "none",
        color: isHoveringEdit ? skaleLinkBlue : skaleMainBlue,
        fontSize: 24,
      }}
      onClick={() => {
        UpduoAnalytics.logEvent("user-skill-module", {
          skillModuleId: skillModule.id,
        });
      }}
      onMouseEnter={() => {
        setIsHoveringEdit(true);
      }}
      onMouseLeave={() => {
        setIsHoveringEdit(false);
      }}
    >
      <div
        style={{
          paddingLeft: 9,
          paddingRight: 9,
          color: disabled ? skaleLightGray : buttonColor,
          paddingBottom: 6,
        }}
      >
        <Icon size={15} icon={pencil} />
      </div>
    </NavLink>
  );
}

export default function SkillModuleDetail({
  skillList,
  skillModule,
  skillModuleIndex,
  canEdit,
  _handleDeactivateSkill,
  _handleCloneSkill,
  _handleCloneSkillModule,
  _handleDeactivateSkillModule,
}) {
  const disabled = false;
  const [isHoveringEdit, setIsHoveringEdit] = useState(false);
  const [isHoveringClone, setIsHoveringClone] = useState(false);
  const [
    isDeleteSkillModuleConfirmationShown,
    setIsDeleteSkillModuleConfirmationShown,
  ] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const deleteSkillModuleConfirmation = (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
      }}
    >
      <Text style={{ color: skaleWebTextGray }}>
        {"Are you sure you want to delete this topic?"}
      </Text>
      <Button
        onPress={() => {
          UpduoAnalytics.logEvent("user-skill-module-delete-cancel", {
            skillModuleId: skillModule.id,
          });
          setIsDeleteSkillModuleConfirmationShown(false);
        }}
        width={80}
        marginLeft={10}
        text={"Keep"}
        height={28}
        borderWidth={1}
        borderColor={skalePrimaryBlue}
        color={skaleWhite}
        textColor={skalePrimaryBlue}
      />
      <Button
        onPress={() => {
          UpduoAnalytics.logEvent("user-skill-module-delete-confirm", {
            skillModuleId: skillModule.id,
          });
          _handleDeactivateSkillModule(skillModuleIndex);
          setIsDeleteSkillModuleConfirmationShown(false);
        }}
        width={120}
        marginLeft={10}
        text={"Delete"}
        height={28}
        borderWidth={1}
        borderColor={skaleRed}
        color={skaleWhite}
        textColor={skaleRed}
      />
    </View>
  );

  const cloneBtnColor = isHoveringClone ? skaleYellow : skaleMainBlue;
  const skillModuleCloneButton = (
    <TouchableOpacity
      onPress={() => {
        UpduoAnalytics.logEvent("user-module-new", {
          isClone: true,
          cloneId: skillModule.id,
        });
        _handleCloneSkillModule(skillModule.id);
      }}
      style={{
        paddingHorizontal: 9,
      }}
      {...Platform.select({
        web: {
          onMouseEnter: () => {
            setIsHoveringClone(true);
          },
          onMouseLeave: () => {
            setIsHoveringClone(false);
          },
        },
      })}
    >
      <div
        style={{
          color: disabled ? skaleLightGray : cloneBtnColor,
          paddingBottom: 2.5,
        }}
      >
        <Icon size={15} icon={clone} />
      </div>
    </TouchableOpacity>
  );

  useEffect(() => {
    window.addEventListener("resize", _handleWindowResize);
    return () => {
      window.removeEventListener("resize", _handleWindowResize);
    };
  }, []);

  const _handleWindowResize = () => {
    setWindowWidth(Dimensions.get("window").width);
  };

  return (
    <View>
      <Draggable
        key={skillModule.id}
        draggableId={`module${skillModule.id}`}
        index={skillModuleIndex}
        type={"module"}
      >
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
          >
            <View
              style={{
                backgroundColor: snapshot.isDragging && skaleWhite,
                borderWidth: 1,
                borderColor: skaleWebBackgroundGray,
                borderRadius: 12,
                flexGrow: 1,
                minHeight: 100,
                marginBottom: 24,
                padding: 32,
              }}
            >
              <View
                style={[
                  {
                    flex: 1,
                    borderColor: skaleWebBackgroundGray,
                    flexDirection: "row",
                  },
                ]}
              >
                <View
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    marginBottom: skillList.length > 0 ? 32 : 0,
                  }}
                >
                  <NavLink
                    to={`/content/topic/${skillModule.id}`}
                    style={{
                      textDecoration: "none",
                      color: isHoveringEdit ? skaleLinkBlue : skaleMainBlue,
                      fontSize: 24,
                      overflow: "hidden",
                      overflowWrap: "break-word",
                      maxWidth: window.innerWidth * 0.6 - 100,
                    }}
                    onClick={() => {
                      UpduoAnalytics.logEvent("user-skill-module", {
                        skillModuleId: skillModule.id,
                      });
                    }}
                    onMouseEnter={() => {
                      setIsHoveringEdit(true);
                    }}
                    onMouseLeave={() => {
                      setIsHoveringEdit(false);
                    }}
                  >
                    {`${skillModule.order + 1}. ${skillModule.name}`}
                  </NavLink>
                  {canEdit ? (
                    <View
                      style={{
                        flexDirection: "row",
                        width: 150,
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginLeft: 12,
                      }}
                    >
                      <SkillEditButton
                        disabled={false}
                        skillModule={skillModule}
                      />
                      {skillModuleCloneButton}
                      <SkillDeleteButton
                        disabled={false}
                        onPress={() => {
                          UpduoAnalytics.logEvent("user-module-delete", {
                            skillModule: skillModule.id,
                          });
                          setIsDeleteSkillModuleConfirmationShown(
                            !isDeleteSkillModuleConfirmationShown
                          );
                        }}
                      />
                    </View>
                  ) : null}
                </View>
                {skillList.length <= 0 ? (
                  <View
                    style={{
                      flex: 1,
                      justifyContent: "center",
                      alignItems: "flex-end",
                    }}
                  >
                    <Text
                      style={{
                        color: skaleTextGray,
                        fontSize: 16,
                        width: windowWidth < 850 ? 180 : 340,
                      }}
                    >
                      {"You have not added any lessons to this topic yet"}
                    </Text>
                  </View>
                ) : null}
              </View>
              <Droppable droppableId={`${skillModuleIndex}`} type={"skill"}>
                {(provided, snapshot) => (
                  <div ref={provided.innerRef}>
                    <View>
                      {skillList.map((item, itemIndex) => (
                        <SkillListing
                          disabled={false}
                          key={itemIndex}
                          skillIndex={itemIndex}
                          canEdit={canEdit}
                          skillObject={item}
                          onPressDeactivate={() => {
                            _handleDeactivateSkill(skillModuleIndex, itemIndex);
                          }}
                          onPressClone={() => {
                            _handleCloneSkill(skillList[itemIndex].id);
                          }}
                          isDragging={snapshot.isDragging}
                        />
                      ))}
                      {provided.placeholder}
                    </View>
                  </div>
                )}
              </Droppable>
              {isDeleteSkillModuleConfirmationShown
                ? deleteSkillModuleConfirmation
                : null}
            </View>
          </div>
        )}
      </Draggable>
    </View>
  );
}
